import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import {
  deleteDoc,
  where,
  collection,
  doc,
  query,
  onSnapshot,
  orderBy
} from 'firebase/firestore';
import { existsWithLength } from '@aldridge/aldg-helpers';
import getWBSDescription, { getManHours } from 'utils/getWBSDescription';
import { firestore } from '../../firebase';
import {
  _CurrentDate,
  _CurrentForeman,
  _CurrentJobNumber
} from '../../_Recoil/atoms';
import { basicStyles } from '../../theme';

const EndDayJobList = (props) => {
  const { history } = props;
  const [newJobs, setNewJobs] = useState([]);
  const theme = useTheme();
  const customClasses = makeStyles(
    () => ({
      tableHead: {
        fontWeight: 'bold',
        padding: '16px',
        cursor: 'default',
        [theme.breakpoints.down('md')]: {
          paddingBottom: '0px'
        }
      }
    }),
    { index: 1 }
  )();
  const classes = basicStyles();
  const CurrentForeman = useRecoilValue(_CurrentForeman);
  const CurrentJobNumber = useRecoilValue(_CurrentJobNumber);
  const CurrentDate = useRecoilValue(_CurrentDate);
  const handleSelect = (idx) => {
    history.push(`/pod/${idx}`);
  };
  const [deleteState, setDeleteState] = useState({
    show: false,
    id: -99
  });

  useEffect(() => {
    let mounted = true;
    if (CurrentDate.value === '') {
      toast.error('Date is Required');
      setNewJobs([]);
      return;
    }
    if (
      CurrentForeman.value.length === 0 &&
      CurrentJobNumber.value.length === 0
    ) {
      toast.error('Foreman or Job Number is Required');
      setNewJobs([]);
      return;
    }
    const colRef = (() => {
      let col = collection(firestore, 'Tasks');
      if (CurrentForeman.value.length > 0)
        col = query(col, where('data.Foreman', '==', CurrentForeman.value[0]));
      if (CurrentDate.value !== '')
        col = query(col, where('data.Date', '==', CurrentDate.value));
      if (CurrentJobNumber.value.length > 0)
        col = query(
          col,
          where('data.JobNumber', '==', CurrentJobNumber.value[0])
        );
      col = query(col, orderBy('CreatedDate'));
      return col;
    })();
    const unsub = onSnapshot(colRef, (res) => {
      const allNewJobs = [];
      res.forEach((d) => allNewJobs.push(d.data()));
      if (mounted) setNewJobs(allNewJobs);
    });
    // eslint-disable-next-line consistent-return
    return () => {
      mounted = false;
      unsub();
    };
  }, [CurrentForeman, CurrentDate, CurrentJobNumber]);

  const removeRecord = async () => {
    if (deleteState.id !== -99) {
      deleteDoc(doc(firestore, 'Tasks', deleteState.id));
      toast.success('P.O.D. Removed.');
    }
    setDeleteState({ show: false, id: -99 });
  };
  const handleDelete = (idx) => {
    setDeleteState({ show: true, id: idx });
  };

  return (
    <>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '32px'
        }}
      />
      <Modal open={deleteState.show} sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Paper sx={{ width: '50%' }}>
          <Card>
            <CardHeader title="Delete P.O.D." />
            <CardContent>
              Would you like you to delete this record?
            </CardContent>
            <CardActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="error" onClick={removeRecord}>Delete</Button>
              <Button variant="contained" onClick={() => { setDeleteState({ show: false, id: -99 }); }}>Close</Button>
            </CardActions>
          </Card>
        </Paper>
      </Modal>
      <Grid container spacing={1}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: 'white' }}>
                <TableCell
                  className={`${customClasses.tableHead} ${classes.tableCell}`}
                >
                  Date
                </TableCell>
                <TableCell
                  className={`${customClasses.tableHead} ${classes.tableCell}`}
                >
                  Foreman
                </TableCell>

                {CurrentJobNumber.value.length === 0 ? (
                  <TableCell className={classes.tableHeader}>
                    Job Number
                  </TableCell>
                ) : null}
                <TableCell
                  className={`${customClasses.tableHead} ${classes.tableCell}`}
                  colSpan={2}
                >
                  P.O.D./WBS
                </TableCell>
                <TableCell
                  className={`${customClasses.tableHead} ${classes.tableCell}`}
                >
                  Planned vs Actual
                </TableCell>
                <TableCell className={customClasses.tableHead} align='right' />
              </TableRow>
            </TableHead>
            <TableBody>
              {newJobs.map((row) => (
                <TableRow key={row.id} className={classes.row1}>
                  <TableCell
                    className={classes.tableCell}
                    onClick={() => handleSelect(row.id)}
                  >
                    {row.data.Date}
                  </TableCell>
                  <TableCell
                    className={classes.tableCell}
                    onClick={() => handleSelect(row.id)}
                  >
                    {row.data.ForemanDisplay}
                  </TableCell>
                  {CurrentJobNumber.value.length === 0 ? (
                    <TableCell
                      className={classes.tableCell}
                      onClick={() => handleSelect(row.id)}
                    >
                      {existsWithLength(row.data.GLCode) ? row.data.GLCode : row.data.JobNumberDisplay}
                    </TableCell>
                  ) : null}
                  <TableCell
                    className={classes.tableCell}
                    onClick={() => handleSelect(row.id)}
                    colSpan={2}
                  >
                    {getWBSDescription(row)}
                  </TableCell>
                  <TableCell
                    className={classes.tableCell}
                    onClick={() => handleSelect(row.id)}
                  >
                    Planned: {row.data.StartDay.ExpectedProductionQuantity}{' '}
                    {row.data.StartDay.UoM} <br />
                    {existsWithLength(
                      row.data.EndDay.CompletedProductionQuantity
                    )
                      ? `Actual: ${row.data.EndDay.CompletedProductionQuantity} ${row.data.StartDay.UoM}`
                      : ''} <br />
                    Man Hours: {getManHours(row)}
                  </TableCell>
                  <TableCell className={classes.tableCell} align='right'>
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(row.id);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </>
  );
};
EndDayJobList.propTypes = {
  history: PropTypes.objectOf(PropTypes.any).isRequired
};
EndDayJobList.defaultProps = {};

export default EndDayJobList;
